/**
 * The ⌘K search's pure parts: which page it was opened on (its context), the
 * order of its sections from there, where a result opens, and the matched
 * words in a title. The palette itself is components/SearchPalette.vue.
 */

import type { LedgerItem, LinkSummary, PersonSummary, SearchResult } from '@satisa-memory/shared'
import type { RouteLocationRaw } from 'vue-router'
import { todoItemId } from './todo'

export type Section = 'library' | 'ledger' | 'people'
/** What the palette searches: everything (the page's own section first), or one section. */
export type Scope = 'all' | Section

export const SCOPES: Scope[] = ['all', 'library', 'ledger', 'people']
export const SECTION_LABELS: Record<Section, string> = {
  library: 'Library',
  ledger: 'Ledger',
  people: 'People'
}

/** The section of the page it was opened on, by the route's path; null on the others. */
export function contextOf(path: string): Section | null {
  if (path === '/' || path.startsWith('/links/')) return 'library'
  if (path.startsWith('/ledger')) return 'ledger'
  if (path.startsWith('/people')) return 'people'
  return null
}

/** The page's own section first, then the rest in a fixed order. */
export function sectionOrder(context: Section | null, scope: Scope): Section[] {
  if (scope !== 'all') return [scope]
  const rest: Section[] = ['ledger', 'library', 'people']
  return context ? [context, ...rest.filter((s) => s !== context)] : rest
}

/** The next scope for Tab (or the previous for shift-Tab). */
export function nextScope(scope: Scope, back = false): Scope {
  const i = SCOPES.indexOf(scope)
  return SCOPES[(i + (back ? SCOPES.length - 1 : 1)) % SCOPES.length]!
}

export type Hit =
  | { section: 'ledger'; id: string; item: LedgerItem }
  | { section: 'library'; id: string; link: LinkSummary }
  | { section: 'people'; id: string; person: PersonSummary }

/**
 * The hits of each section, in order. The owner's own to-do notes are left
 * out of the library: their item, in the ledger, is the thing he is after.
 */
export function hitsOf(r: SearchResult): Record<Section, Hit[]> {
  return {
    ledger: r.items.map((item) => ({ section: 'ledger', id: item.id, item })),
    library: r.links
      .filter((link) => !todoItemId(link))
      .map((link) => ({ section: 'library', id: link.id, link })),
    people: r.people.map((person) => ({ section: 'people', id: person.id, person }))
  }
}

/**
 * Where a hit opens. An item opens in the view that lists it, so it is
 * selected in the list beside it: a decision in Decisions, a closed one in
 * Closed.
 */
export function hitTo(hit: Hit): RouteLocationRaw {
  switch (hit.section) {
    case 'ledger': {
      const i = hit.item
      const view = i.status !== 'open' ? 'closed' : i.kind === 'decision' ? 'decisions' : 'all'
      return {
        name: 'ledger-item',
        params: { itemId: i.id },
        query: view === 'all' ? {} : { view }
      }
    }
    case 'library':
      return { name: 'link', params: { id: hit.id } }
    case 'people':
      return { name: 'person', params: { id: hit.id } }
  }
}

/** A section's own page, searched for the same words. */
export function sectionTo(section: Section, q: string): RouteLocationRaw {
  const query = { q: q.trim() }
  if (section === 'ledger') return { name: 'ledger', query }
  if (section === 'people') return { name: 'people', query }
  return { name: 'home', query }
}

/** The words searched for, as the Worker reads them (links.ts ftsQuery): prefixes, any case. */
export function terms(q: string): string[] {
  return q
    .normalize('NFKC')
    .split(/[^\p{L}\p{N}_@.-]+/u)
    .filter(Boolean)
}

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/**
 * The text cut into runs, marking each word that starts with a searched
 * term — the way FTS matched it, accents and case aside.
 */
export function highlight(text: string, q: string): { text: string; hit: boolean }[] {
  const ts = terms(q).map(fold)
  if (!ts.length) return [{ text, hit: false }]
  const runs: { text: string; hit: boolean }[] = []
  for (const part of text.split(/([\p{L}\p{N}_@.-]+)/u)) {
    if (!part) continue
    const f = fold(part)
    const matched = ts.filter((t) => f.startsWith(t))
    if (!matched.length) {
      runs.push({ text: part, hit: false })
      continue
    }
    // Only the matched prefix is marked: "pric" in "pricing" — unless folding
    // changed the word's length (a decomposed accent), then all of it.
    const len = Math.max(...matched.map((t) => t.length))
    const cut = fold(part.slice(0, len)) === f.slice(0, len) ? len : part.length
    runs.push({ text: part.slice(0, cut), hit: true })
    if (cut < part.length) runs.push({ text: part.slice(cut), hit: false })
  }
  return runs
}

/** "⌘K" on a Mac, "Ctrl K" elsewhere. */
export function shortcutLabel(platform: string): string {
  return /Mac|iPhone|iPad/i.test(platform) ? '⌘K' : 'Ctrl K'
}
